export interface InfoItem {
  label: string
  value: string
}

export interface InfoSection {
  title: string
  items: InfoItem[]
}

export interface PersonalInfo {
  name: string
  bio: string
  location: string
  website: string
  linkedIn: string
}

export interface FounderStartupInfo {
  startupName: string
  description: string
  category: string
  startupWebsite: string
  productStage: string
  arr: string
  founded: string
  tags: string
  northStarMetricName: string
  northStarMetricValue: string
  isFundraising: boolean
  fundraisingStage: string
  fundraisingAmount: string
}

export interface InvestorProfileInfo {
  fundName: string
  fundWebsite: string
  investmentAreas: string
  investmentRegions: string
}

export interface FounderFormData {
  personal: PersonalInfo
  startup: FounderStartupInfo
}

export interface InvestorFormData {
  personal: PersonalInfo
  profile: InvestorProfileInfo
}

export type FormState =
  | { role: 'founder'; data: FounderFormData }
  | { role: 'investor'; data: InvestorFormData }

// Map Claude's arbitrary labels to canonical labels
const LABEL_ALIASES: Record<string, string> = {
  'full name': 'Name',
  'user name': 'Name',
  biography: 'Bio',
  about: 'Bio',
  summary: 'Bio',
  'website url': 'Website',
  'personal website': 'Website',
  'linkedin url': 'LinkedIn',
  'linkedin profile': 'LinkedIn',
  'startup description': 'Description',
  'company name': 'Startup Name',
  'company description': 'Description',
  'startup website': 'Startup Website',
  'startup website url': 'Startup Website',
  'company website': 'Startup Website',
  'startup location': 'Location',
  categories: 'Category',
  category: 'Category',
  'founding date': 'Founded',
  founded: 'Founded',
  'founded date': 'Founded',
  date: 'Founded',
  tag: 'Tags',
  tags: 'Tags',
  'is fundraising': 'Are you fundraising?',
  fundraising: 'Are you fundraising?',
  'fundraising status': 'Are you fundraising?',
  'fundraising stage': 'Fundraising Stage',
  round: 'Fundraising Stage',
  'raise stage': 'Fundraising Stage',
  'funding round': 'Fundraising Stage',
  'fundraising amount': 'Fundraising Amount',
  stage: 'Product Stage',
  'product stage': 'Product Stage',
  arr: 'ARR',
  'current arr': 'ARR',
  'annual recurring revenue': 'ARR',
  revenue: 'ARR',
  'north star metric name': 'North Star Metric Name',
  'metric name': 'North Star Metric Name',
  'traction metric': 'North Star Metric Name',
  'traction metric name': 'North Star Metric Name',
  'north star metric value': 'North Star Metric Value',
  'metric value': 'North Star Metric Value',
  'traction metric value': 'North Star Metric Value',
  'fund name': 'Fund Name',
  'firm name': 'Fund Name',
  'fund website': 'Fund Website',
  'fund website url': 'Fund Website',
  'firm website': 'Fund Website',
  'investment areas': 'Investment Areas',
  'investment focus': 'Investment Areas',
  'investment regions': 'Investment Regions',
  'investment geographies': 'Investment Regions',
  geographies: 'Investment Regions',
}

function canonicalLabel(label: string): string {
  return LABEL_ALIASES[label.toLowerCase().trim()] ?? label
}

export function parseToMonthInput(value: string): string {
  const isoMatch = value.match(/^(\d{4})-(\d{2})/)
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}`

  const months: Record<string, string> = {
    january: '01',
    february: '02',
    march: '03',
    april: '04',
    may: '05',
    june: '06',
    july: '07',
    august: '08',
    september: '09',
    october: '10',
    november: '11',
    december: '12',
  }
  const nameMatch = value.match(/^(\w+)\s+(\d{4})$/i)
  if (nameMatch?.[1] && nameMatch[2]) {
    const monthName = nameMatch[1].toLowerCase()
    const year = nameMatch[2]
    const monthKey = Object.keys(months).find((m) => m.startsWith(monthName))
    if (monthKey) return `${year}-${months[monthKey]}`
  }

  return value
}

const URL_LABELS = new Set(['Website', 'Startup Website', 'Fund Website', 'LinkedIn'])

function buildValueMap(sections: InfoSection[]): Map<string, string> {
  const map = new Map<string, string>()

  for (const section of sections) {
    for (const item of section.items) {
      const normalized = canonicalLabel(item.label)

      // Handle combined "North Star Metric" → split into Name + Value
      if (/^north\s*star\s*metric$/i.test(normalized) || /^north\s*star$/i.test(item.label)) {
        const colonIndex = item.value.indexOf(':')
        if (colonIndex > 0) {
          map.set('North Star Metric Name', item.value.slice(0, colonIndex).trim())
          map.set('North Star Metric Value', item.value.slice(colonIndex + 1).trim())
        } else {
          const dashIndex = item.value.indexOf(' - ')
          if (dashIndex > 0) {
            map.set('North Star Metric Name', item.value.slice(0, dashIndex).trim())
            map.set('North Star Metric Value', item.value.slice(dashIndex + 3).trim())
          } else {
            map.set('North Star Metric Name', item.value)
            map.set('North Star Metric Value', '')
          }
        }
      } else if (item.value) {
        let val = item.value
        if (URL_LABELS.has(normalized) && !/^https?:\/\//i.test(val)) {
          val = `https://${val}`
        }
        map.set(normalized, val)
      }
    }
  }

  return map
}

export function detectRole(sections: InfoSection[]): 'founder' | 'investor' {
  const m = buildValueMap(sections)
  if (m.has('Fund Name') && !m.has('Startup Name') && !m.has('Description') && !m.has('Category')) {
    return 'investor'
  }
  return 'founder'
}

export function parseFounderFormData(sections: InfoSection[]): FounderFormData {
  const m = buildValueMap(sections)
  return {
    personal: {
      name: m.get('Name') ?? '',
      bio: m.get('Bio') ?? '',
      location: m.get('Location') ?? '',
      website: m.get('Website') ?? '',
      linkedIn: m.get('LinkedIn') ?? '',
    },
    startup: {
      startupName: m.get('Startup Name') ?? '',
      description: m.get('Description') ?? '',
      category: m.get('Category') ?? '',
      startupWebsite: m.get('Startup Website') ?? '',
      productStage: m.get('Product Stage') ?? '',
      arr: (m.get('ARR') ?? '').replace(/[^0-9]/g, ''),
      founded: parseToMonthInput(m.get('Founded') ?? ''),
      tags: m.get('Tags') ?? '',
      northStarMetricName: m.get('North Star Metric Name') ?? '',
      northStarMetricValue: m.get('North Star Metric Value') ?? '',
      isFundraising: /^(true|yes|1)$/i.test(m.get('Are you fundraising?') ?? 'No'),
      fundraisingStage: m.get('Fundraising Stage') ?? '',
      fundraisingAmount: (m.get('Fundraising Amount') ?? '').replace(/[^0-9]/g, ''),
    },
  }
}

export function parseInvestorFormData(sections: InfoSection[]): InvestorFormData {
  const m = buildValueMap(sections)
  return {
    personal: {
      name: m.get('Name') ?? '',
      bio: m.get('Bio') ?? '',
      location: m.get('Location') ?? '',
      website: m.get('Website') ?? '',
      linkedIn: m.get('LinkedIn') ?? '',
    },
    profile: {
      fundName: m.get('Fund Name') ?? '',
      fundWebsite: m.get('Fund Website') ?? '',
      investmentAreas: m.get('Investment Areas') ?? '',
      investmentRegions: m.get('Investment Regions') ?? '',
    },
  }
}

export function serializeFounderForm(data: FounderFormData): InfoSection[] {
  return [
    {
      title: 'Personal Info',
      items: [
        { label: 'Name', value: data.personal.name },
        { label: 'Bio', value: data.personal.bio },
        { label: 'Location', value: data.personal.location },
        { label: 'Website', value: data.personal.website },
        { label: 'LinkedIn', value: data.personal.linkedIn },
      ],
    },
    {
      title: 'Startup',
      items: [
        { label: 'Startup Name', value: data.startup.startupName },
        { label: 'Description', value: data.startup.description },
        { label: 'Category', value: data.startup.category },
        { label: 'Startup Website', value: data.startup.startupWebsite },
        { label: 'Product Stage', value: data.startup.productStage },
        { label: 'ARR', value: data.startup.arr },
        { label: 'Founded', value: data.startup.founded },
        { label: 'Tags', value: data.startup.tags },
        { label: 'North Star Metric Name', value: data.startup.northStarMetricName },
        { label: 'North Star Metric Value', value: data.startup.northStarMetricValue },
        { label: 'Are you fundraising?', value: data.startup.isFundraising ? 'Yes' : 'No' },
        ...(data.startup.isFundraising
          ? [
              { label: 'Fundraising Stage', value: data.startup.fundraisingStage },
              { label: 'Fundraising Amount', value: data.startup.fundraisingAmount },
            ]
          : []),
      ],
    },
  ]
}

export function serializeInvestorForm(data: InvestorFormData): InfoSection[] {
  return [
    {
      title: 'Personal Info',
      items: [
        { label: 'Name', value: data.personal.name },
        { label: 'Bio', value: data.personal.bio },
        { label: 'Location', value: data.personal.location },
        { label: 'Website', value: data.personal.website },
        { label: 'LinkedIn', value: data.personal.linkedIn },
      ],
    },
    {
      title: 'Investment Profile',
      items: [
        { label: 'Fund Name', value: data.profile.fundName },
        { label: 'Fund Website', value: data.profile.fundWebsite },
        { label: 'Investment Areas', value: data.profile.investmentAreas },
        { label: 'Investment Regions', value: data.profile.investmentRegions },
      ],
    },
  ]
}

export function parseFormState(sections: InfoSection[]): FormState {
  const role = detectRole(sections)
  return role === 'founder'
    ? { role: 'founder', data: parseFounderFormData(sections) }
    : { role: 'investor', data: parseInvestorFormData(sections) }
}
